<template>
  <PortalWidget
    v-if="hasRpc"
    :widget="{title: computedTitle, iconClass: 'fa fa-gear'}"
  >
    <template v-slot:headerContent v-if="!showSpecificRecordDetails">
      <div class="header-extra">
        <input
          v-model="filters.name.value"
          type="search"
          :placeholder="$t('SEARCH_PREDICTIVE_ROUTING')"
          class="form-control input-md widget-header-input m-r-10"
        >
      </div>
    </template>
    <div
      v-if="!loading"
      class="m-t-10 m-b-15"
    >
      <button
        v-if="!showSpecificRecordDetails"
        type="button"
        class="btn btn-success btn-sm m-r-5"
        :disabled="showCreateForm"
        @click="displayCreateForm"
      >
        <i class="fa fa-plus" />
        <span v-t="'BUTTON_CREATE_PREDICTIVE_ROUTING'" />
      </button>

      <a
        v-if="showSpecificRecordDetails"
        ui-sref="dashboard.predictive-routing"
        class="btn btn-default btn-sm"
        href="#/dashboard/predictive-routing"
      >
        <i class="fa fa-arrow-left"></i>
        <span v-t="'RPC_PREDICTIVE_ROUTING_SHOW_ALL'" />
      </a>
    </div>
    <!-- Table Container -->
    <div
      v-if="!loading"
      class="grid_table_holder"
    >
      <v-table
        :data="predictiveRouting"
        :current-page.sync="currentPage"
        :filters="filters"
        :page-size="20"
        :hide-sort-icons="true"
        class="table_beauty"
        @totalPagesChanged="totalPages = $event"
      >
        <thead slot="head">
          <tr>
            <th v-if="isMobile" />

            <v-th
              default-sort="asc"
              sort-key="name"
              v-t="'TABLE_COL_NAME'"
            />

            <v-th
              default-sort="asc"
              sort-key="type"
              v-t="'TABLE_COL_TYPE'"
            />
            <th
              v-if="!isMobile"
              v-t="'TABLE_COL_ACTIONS'"
            />
          </tr>
        </thead>
        <tbody
          slot="body"
          slot-scope="{ displayData }"
        >

          <tr v-if="!showCreateForm && !predictiveRouting.length">
            <td colspan="100" v-t="'PREDICTIVE_ROUTING_EMPTY'"></td>
          </tr>
          <!-- Create New Form -->
          <tr
            v-if="showCreateForm"
            class="row-inline-new-form slide-down bg-row-even"
          >
            <td colspan="100">
              <ERPCSettingsForm
                :current-predictive-routing-config="predictiveRoutingConfigToDuplicate"
                :show-loader="createPredictiveRoutingConfigStatus.status === 'pending'"
                :is-table-view="true"
                @submit="createPredictiveRouting"
                @cancel="hideCreateForm"
              />
            </td>
          </tr>

          <!-- Rows -->
          <template v-for="(row, index) in displayData">
            <tr
              v-if="!(isMobile && activeActionRowId == row.id)"
              :key="row.id"
              :class="{
                'bg-row-odd': index % 2 === 0,
                'bg-row-even': index % 2 !== 0,
              }"
            >
              <!-- Mobile Actions -->
              <td v-if="isMobile">
                <button
                  class="btn btn-list-function btn-expand-actions"
                  @click="displayMobileAction(row.id)"
                >
                  <i class="fa fa-ellipsis-v" />
                </button>
              </td>
              <!-- Name -->
              <td>
                <a
                  :href="`#/dashboard/predictive-routing/${row.id}`"
                  @click="displayEditForm(row.id, $event)"
                >
                  {{ row.name }}
                </a>
              </td>

              <!-- Type -->
              <td>
                {{ configType(row) }}
              </td>

              <!-- Desktop Actions -->
              <td v-if="!isMobile">
                <PredictiveRoutingActions
                  :current-predictive-routing="row"
                  :disabled="disabled"
                  @deletePredictiveRouting="confirmDeletePredictiveRouting"
                  @editPredictiveRouting="displayEditForm(row.id)"
                  @duplicatePredictiveRouting="duplicatePredictiveRouting(row.id)"
                  @showPredictiveRoutingReferences="displayReferences(row.id)"
                />
              </td>
            </tr>

            <tr
              v-if="isMobile && activeActionRowId == row.id"
              :key="row.id + '-mobile'"
              class="mobile-actions slide-right"
            >
              <td colspan="10">
                <PredictiveRoutingActions
                  :current-predictive-routing="row"
                  :disabled="disabled"
                  @deletePredictiveRouting="confirmDeletePredictiveRouting"
                  @collapseActions="!disabled && hideMobileAction()"
                  @editPredictiveRouting="displayEditForm(row.id)"
                  @duplicatePredictiveRouting="duplicatePredictiveRouting(row.id)"
                  @showPredictiveRoutingReferences="displayReferences(row.id)"
                />
              </td>
            </tr>
            <!-- References -->
            <!-- Commenting out for initial release, this will be coming right on the heels of first release -->
            <!-- <tr
              v-if="showReferences(row.id)"
              :key="row.id + '-references'"
              class="row-inline-new-form slide-down bg-row-even"
            >
              <td colspan="100">
                <div class="p-l-15 p-r-15">
                  <legend class="f-s-13 p-b-10 p-l-0">
                    <span v-t="'REFERENCES'" />
                    <button
                      type="button"
                      class="btn btn-xs btn-danger legend-right-button"
                      @click="hideReferences"
                    >
                      <i aria-hidden="true" class="fa fa-close" />
                      Close
                    </button>
                  </legend>
                  <PredictiveRoutingConfigReferences
                    :config-id="row.id"
                  />
                </div>
              </td>
            </tr> -->
            <!-- References -->
            <tr
              v-if="showEditForm(row.id)"
              :key="row.id + '-edit'"
              class="row-inline-new-form slide-down bg-row-even"
            >
              <td colspan="100">
                <ERPCSettingsForm
                  :show-loader="updatePredictiveRoutingConfigStatus.status == 'pending'"
                  :current-predictive-routing-config="row"
                  :is-new-config="false"
                  :is-table-view="true"
                  @submit="updatePredictiveRouting"
                  @cancel="hideEditForm"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </v-table>
      <div v-if="predictiveRouting && predictiveRouting.length" class="pagination-min">
        <div class="text-center p-7">
          <smart-pagination
            :current-page.sync="currentPage"
            :total-pages="totalPages"
            :max-page-links="7"
          />
        </div>
      </div>
    </div>
    <div
      v-else
      class="text-center empty-records"
    >
      <Loader />
      <span v-t="'PREDICTIVE_ROUTING_TABLE_LOADING'" />
    </div>
    <PredictiveRoutingConfigDeleteModalBasic
      v-if="showConfirmation"
      :predictive-routing-config="predictiveRoutingConfigToDelete"
      :disable-modal-confirm="disableModalConfirm"
      :deleting-predictive-routing-config="deletingPredictiveRoutingConfig"
      :references="references"
      :has-references="hasReferences"
      :config-delete-force.sync="predictiveRoutingConfigDeleteForce"
      @closeModal="closeModal"
      @attestDelete="attestDelete"
      @deletePredictiveRoutingConfig="deletePredictiveRouting"
    />
  </PortalWidget>
</template>

<script>
import messages from '../../locales';
import sharedMessages from '@/core/locales';
import { isMobile } from '@/core/utilities';
import PortalWidget from '@/core/components/PortalWidget.vue';
import Loader from '@/core/components/Loader';
import PredictiveRoutingActions from './PredictiveRoutingActions';
import ERPCSettingsForm from '@/core/components/RPCSettings/ERPCSettings/ERPCSettingsForm';
import PredictiveRoutingConfigReferences from './PredictiveRoutingConfigReferences';

// Basic delete modal for first release, modal with inbound references will be releaased right after
import PredictiveRoutingConfigDeleteModalBasic from './PredictiveRoutingConfigDeleteModalBasic';

import nzToggle from '@/core/components/nzToggle/nzToggle';
import { utilities, $ngStateService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
import { STATUS_ERROR, STATUS_SUCCESS, STATUS_PENDING } from '@/store/helpers';
import * as actionTypes from '@/modules/predictiveRouting/store/actionTypes';
export default {
  components: {
    PortalWidget,
    Loader,
    nzToggle,
    PredictiveRoutingActions,
    ERPCSettingsForm,
    PredictiveRoutingConfigReferences,
    PredictiveRoutingConfigDeleteModalBasic
  },
  data() {
    return {
      currentPage: 1,
      totalPages: 0,
      isMobile: isMobile,
      activeActionRowId: null,
      activeEditRowId: null,
      activeReferencesRowId: null,
      predictiveRoutingConfigToDuplicate: undefined,
      predictiveRoutingConfigToDelete: null,
      predictiveRoutingConfigDeleteForce: false,
      showCreateForm: false,
      showConfirmation: false,
      showSpecificRecordDetails: false,
      filters: {
        name: { value: '', keys: ['name', 'id'] }
      },
    }
  },
  watch: {
    currentPage() {
      this.hideEditForm();
      this.showCreateForm = false;
    },
    filters: {
      immediate: true,
      deep: true,
      handler() {
        this.hideEditForm();
        this.showCreateForm = false;
      }
    }
  },
  async created() {
    this.showSpecificRecordDetails = typeof this.$attrs.configid == 'string' ? this.$attrs.configid : null;
    if (this.showSpecificRecordDetails) {
      const predictiveRoutingConfig = await this.getPredictiveRoutingConfig(this.$attrs.configid);
      this.SET_PREDICTIVE_ROUTING([predictiveRoutingConfig]);
      this.displayEditForm(predictiveRoutingConfig.id);
      return;
    }
    this.getPredictiveRouting();
    this.$attrs.configid && this.$attrs.configid.duplicate == true && this.duplicatePredictiveRouting(this.$attrs.configid.id);
  },
  computed: {
    ...mapState('predictiveRouting', [
      'predictiveRouting',
      'references'
    ]),
    ...mapState({
      flags: 'currentAccountFeatureFlags'
    }),
    ...mapGetters('predictiveRouting', [
      'getPredictiveRoutingStatus',
      'getPredictiveRoutingConfigStatus',
      'createPredictiveRoutingConfigStatus',
      'updatePredictiveRoutingConfigStatus',
      'deletePredictiveRoutingConfigStatus',
      'getPredictiveRoutingConfigReferencesStatus'
    ]),
    computedTitle() {
      return this.showSpecificRecordDetails && this.predictiveRouting[0] ? this.$t('HEADER_PREDICTIVE_ROUTING') + ': ' + this.predictiveRouting[0].name : this.$t('HEADER_PREDICTIVE_ROUTING');
    },
    hasRpc() {
      return !!this.flags.rpc;
    },
    loading() {
      return this.getPredictiveRoutingStatus.status == STATUS_PENDING;
    },
    deletingPredictiveRoutingConfig() {
      return this.deletePredictiveRoutingConfigStatus.status === STATUS_PENDING;
    },
    disabled() {
      return (
        this.showSpecificRecordDetails ? false :
        this.createPredictiveRoutingConfigStatus.status == STATUS_PENDING
        || this.updatePredictiveRoutingConfigStatus.status == STATUS_PENDING
        || this.showCreateForm
        || !!this.activeEditRowId
        || !!this.activeReferencesRowId
      );
    },
    hasReferences() {
      return this.references && this.references.length > 0;
    },
    disableModalConfirm() {
      return (
        this.getPredictiveRoutingConfigReferencesStatus.status == STATUS_PENDING ||
        this.getPredictiveRoutingConfigReferencesStatus.status == STATUS_ERROR ||
        this.deletingPredictiveRoutingConfig ||
        !this.predictiveRoutingConfigDeleteForce
      );
    },
  },
  methods: {
    ...mapActions('predictiveRouting', Object.values(actionTypes)),
    ...mapMutations('predictiveRouting',['SET_PREDICTIVE_ROUTING']),
    displayMobileAction(id) {
      this.activeActionRowId = id;
    },
    hideMobileAction() {
      this.activeActionRowId = null;
    },
    displayCreateForm() {
      this.hideEditForm();
      this.showCreateForm = true;
    },
    displayEditForm(id, $event) {
      $event?.preventDefault();

      this.hideCreateForm();
      this.hideReferences();
      this.activeEditRowId = id;
    },
    showEditForm(id) {
      return !this.showCreateForm && this.activeEditRowId == id;
    },
    displayReferences(id) {
      this.hideCreateForm();
      this.hideEditForm();
      this.activeReferencesRowId = id;
    },
    async duplicatePredictiveRouting(id) {
      if (this.showSpecificRecordDetails) {
        $ngStateService.go('dashboard.predictive-routing', {
          configId: {
            id: id,
            duplicate: true
          }
        });
      }
      this.predictiveRoutingConfigToDuplicate = await this.getPredictiveRoutingConfig(id);
      delete this.predictiveRoutingConfigToDuplicate.id;
      if (this.getPredictiveRoutingConfigStatus.status === STATUS_SUCCESS) {
        this.predictiveRoutingConfigToDuplicate.name = this.predictiveRoutingConfigToDuplicate.name + ' - Copy';
        this.displayCreateForm();
      }
      if (this.getPredictiveRoutingConfigStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.getPredictiveRoutingConfigStatus.error);
      }
    },
    showReferences(id) {
      return !this.showCreateForm && this.activeReferencesRowId == id;
    },
    hideCreateForm() {
      this.predictiveRoutingConfigToDuplicate = undefined;
      this.showCreateForm = false;
    },
    hideEditForm() {
      this.activeEditRowId = null;
      if (this.showSpecificRecordDetails) {
        $ngStateService.go('dashboard.predictive-routing');
      }
    },
    hideReferences() {
      this.activeReferencesRowId = null;
    },
    closeModal() {
      this.showConfirmation = false;
    },
    attestDelete(value) {
      this.predictiveRoutingConfigDeleteForce = value;
    },
    async createPredictiveRouting(config) {
      await this.createPredictiveRoutingConfig(config);
      if (this.createPredictiveRoutingConfigStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('MESSAGE_CREATE_SUCCESS'));
        this.hideCreateForm();
      }
      if (this.createPredictiveRoutingConfigStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.createPredictiveRoutingConfigStatus.error);
      }
    },
    async updatePredictiveRouting(config) {
      await this.updatePredictiveRoutingConfig(config);
      if (this.updatePredictiveRoutingConfigStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('MESSAGE_UPDATE_SUCCESS'));
        if (!this.showSpecificRecordDetails) this.hideEditForm();
      }
      if (this.updatePredictiveRoutingConfigStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.updatePredictiveRoutingConfigStatus.error);
      }
    },
    async confirmDeletePredictiveRouting(config) {
      this.predictiveRoutingConfigToDelete = config;
      this.showConfirmation = true;
      // Commenting out, inbound references will be implemented right after original deploy
      // await this.getPredictiveRoutingConfigReferences(config.id);
    },
    async deletePredictiveRouting(config) {
      await this.deletePredictiveRoutingConfig(config.id);
      if (this.deletePredictiveRoutingConfigStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(
          STATUS_SUCCESS,
          this.$i18n.t('MESSAGE_DELETE_SUCCESS')
        );
        this.getPredictiveRouting();
      }
      if (this.deletePredictiveRoutingConfigStatus.status === STATUS_ERROR) {
        utilities.showToaster(
          STATUS_ERROR,
          this.deletePredictiveRoutingConfigStatus.error
        );
      }
      this.predictiveRoutingConfigToDelete = null;
      this.showConfirmation = false;
    },
    configType(config) {
      if (!config?.setting?.useERPC) return this.$i18n.t('TYPE_REVENUE');
      return config?.setting?.useBasicSetting ? this.$i18n.t('BASIC_TEXT') : this.$i18n.t('ADVANCED_TEXT');
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en',
  },
};
</script>
